import { IsOptional, IsString, IsUUID, MaxLength } from 'class-validator';
import type { Evidence } from './evidence.dto';

/**
 * Evidence photo markup, before/after pairs and the photo timeline.
 *
 * A marked-up photo is a NEW evidence row derived from its original: the server
 * renders the shapes onto the original's bytes (after checking those bytes
 * still match the SHA-256 recorded at upload) and stores the result as a PDF
 * with its own SHA-256. The original row and object are never touched.
 *
 * Off by default per municipality (`evidenceMarkupEnabled`, admin portal).
 */

export const MARKUP_SHAPES = ['arrow', 'ellipse', 'rect', 'freehand', 'text', 'marker'] as const;
export type MarkupShape = (typeof MARKUP_SHAPES)[number];

/** Ink names; each maps to `colors.markup[name]` in @munifocus/tokens. */
export const MARKUP_COLOURS = ['red', 'yellow', 'blue', 'white', 'black'] as const;
export type MarkupColour = (typeof MARKUP_COLOURS)[number];

/** Most shapes on one copy; past this it is a drawing, not a callout. */
export const MAX_MARKUP_ANNOTATIONS = 50;
/** Most points in one freehand stroke. */
export const MAX_MARKUP_POINTS = 500;
/** Longest text callout. */
export const MAX_MARKUP_TEXT = 200;

/**
 * One shape. Points are fractions of the photo's width and height (0–1, origin
 * top-left), so a shape drawn on a phone-sized preview lands in the same place
 * on the full-resolution photo.
 *
 * - arrow: [tail, head]
 * - ellipse / rect: two opposite corners of the bounding box
 * - freehand: the stroke, in order
 * - text / marker: [anchor]; `text` carries the words (a marker's are optional
 *   and shown as its label)
 */
export interface MarkupAnnotation {
  kind: MarkupShape;
  colour: MarkupColour;
  points: Array<[number, number]>;
  text?: string;
}

/**
 * The multipart/JSON body of `POST /files/evidence/:evidenceId/markup`.
 * `annotations` is the JSON-encoded `MarkupAnnotation[]`.
 */
export class CreateEvidenceMarkupDto {
  @IsString()
  @MaxLength(200_000)
  annotations!: string;

  @IsOptional()
  @IsString()
  @MaxLength(2000)
  note?: string;
}

/** `POST /files/:id/evidence-pairs`. */
export class CreateEvidencePairDto {
  @IsUUID()
  beforeEvidenceId!: string;

  @IsUUID()
  afterEvidenceId!: string;

  @IsOptional()
  @IsString()
  @MaxLength(500)
  caption?: string;
}

export interface EvidencePair {
  id: string;
  fileId: string;
  beforeEvidenceId: string;
  afterEvidenceId: string;
  caption: string | null;
  createdAt: string;
  createdByName: string | null;
}

/** One photo on the timeline, with who took it and its integrity record. */
export interface TimelinePhoto extends Evidence {
  takenByName: string | null;
  /** `recorded` when a SHA-256 was taken at upload; `unrecorded` for older rows. */
  integrity: 'recorded' | 'unrecorded';
  /** Marked-up copies drawn on this photo, oldest first. */
  markups: Evidence[];
}

export interface TimelineVisit {
  id: string;
  status: string;
  startedAt: string | null;
  endedAt: string | null;
  officerName: string | null;
}

/** Photos grouped by the site visit they were taken during; `visit: null` for the rest. */
export interface TimelineGroup {
  visit: TimelineVisit | null;
  photos: TimelinePhoto[];
}

/** `GET /files/:id/photo-timeline`. Empty with `enabled: false` while the module is off. */
export interface PhotoTimeline {
  enabled: boolean;
  groups: TimelineGroup[];
  pairs: EvidencePair[];
}

/**
 * Parse and validate a submitted annotation list. Returns the clean list or an
 * error code; never throws. Shared by the API and the clients' own checks.
 */
export function parseMarkupAnnotations(
  raw: unknown,
): { ok: true; annotations: MarkupAnnotation[] } | { ok: false; error: string } {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return { ok: false, error: 'markup_invalid_json' };
    }
  }
  if (!Array.isArray(value) || value.length === 0) return { ok: false, error: 'markup_empty' };
  if (value.length > MAX_MARKUP_ANNOTATIONS) return { ok: false, error: 'markup_too_many' };
  const out: MarkupAnnotation[] = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') return { ok: false, error: 'markup_shape' };
    const a = item as Record<string, unknown>;
    if (!MARKUP_SHAPES.includes(a['kind'] as MarkupShape))
      return { ok: false, error: 'markup_shape' };
    if (!MARKUP_COLOURS.includes(a['colour'] as MarkupColour)) {
      return { ok: false, error: 'markup_colour' };
    }
    const kind = a['kind'] as MarkupShape;
    const points = a['points'];
    if (!Array.isArray(points) || points.length > MAX_MARKUP_POINTS) {
      return { ok: false, error: 'markup_points' };
    }
    const clean: Array<[number, number]> = [];
    for (const p of points) {
      if (!Array.isArray(p) || p.length !== 2) return { ok: false, error: 'markup_points' };
      const [x, y] = p as unknown[];
      if (
        typeof x !== 'number' ||
        typeof y !== 'number' ||
        !Number.isFinite(x) ||
        !Number.isFinite(y)
      ) {
        return { ok: false, error: 'markup_points' };
      }
      if (x < 0 || x > 1 || y < 0 || y > 1) return { ok: false, error: 'markup_points' };
      clean.push([x, y]);
    }
    const need = kind === 'text' || kind === 'marker' ? 1 : kind === 'freehand' ? 2 : 2;
    if (kind === 'freehand' ? clean.length < need : clean.length !== need) {
      return { ok: false, error: 'markup_points' };
    }
    const text = typeof a['text'] === 'string' ? a['text'].trim() : '';
    if (text.length > MAX_MARKUP_TEXT) return { ok: false, error: 'markup_text' };
    if (kind === 'text' && !text) return { ok: false, error: 'markup_text' };
    out.push({
      kind,
      colour: a['colour'] as MarkupColour,
      points: clean,
      ...(text ? { text } : {}),
    });
  }
  return { ok: true, annotations: out };
}

/** Whether a photo can be marked up: an original JPEG or PNG (the formats the renderer embeds). */
export function canMarkUp(e: Pick<Evidence, 'contentType' | 'derivedFromId'>): boolean {
  const t = e.contentType.toLowerCase();
  return !e.derivedFromId && (t === 'image/jpeg' || t === 'image/jpg' || t === 'image/png');
}
